import React, { useEffect, useMemo, useRef, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { usePageContextType } from '@atlassian/rovo-conversation-actions/rovo-conversation-starters';
import { PageContextType } from '@atlassian/rovo-conversation-actions/types';

import { isBrowser } from '../../common/utils/is-browser';
import {
	canPersistOnboardingHoldoutState,
	passesOnboardingHoldout,
	recordOnboardingExposureForHoldout,
} from '../chat-onboarding-holdout';

// If productionized - will consider relocating logic to conversion-assistant-onboarding -- tracking ticket: https://hello.jira.atlassian.cloud/browse/MOOG-146

const ELIGIBLE_PAGES = [
	PageContextType.JIRA_FOR_YOU,
	PageContextType.JIRA_ISSUE_VIEW,
	PageContextType.JIRA_PROJECT,
	PageContextType.CONFLUENCE_PAGE_VIEW,
	PageContextType.CONFLUENCE_BLOG_VIEW,
];
const ELIGIBLE_PRODUCTS = ['jira', 'confluence'];
const DWELL_TIME_MS = 5000;

export const OnboardingEnablement = ({
	product,
}: {
	product: string;
}): React.JSX.Element | null => {
	if (
		isBrowser() &&
		(fg('rovo_onboarding_modal_jira_chat_fg') || fg('rovo_onboarding_modal_confluence_chat_fg'))
	) {
		return <OnboardingEnablementInner product={product} />;
	}

	return null;
};

const OnboardingEnablementInner = ({ product }: { product: string }) => {
	useOnboardingEnablement({ product });
	return null;
};

function useOnboardingEnablement({ product }: { product: string }): void {
	const pageContextType = usePageContextType();
	const { isRovoEnabled } = useRovoEntitlement();
	const { mounted: isChatOpen, setIsChatOnboardingMiniModalOpen } = useUIStore();
	const publish = usePublish('ai-mate');

	const hasOpenedChatRef = useRef(false);
	const routeDwellTimerRef = useRef<NodeJS.Timeout | null>(null);
	const [hasDwelled, setHasDwelled] = useState(false);

	const isCookiePolicyGateEnabled = fg('rovo-onboarding-cookie-policy');
	const [passesOnboardingHoldoutEligibility, setPassesOnboardingHoldoutEligibility] = useState(
		() => (isCookiePolicyGateEnabled ? false : passesOnboardingHoldout()),
	);

	useEffect(() => {
		if (!isCookiePolicyGateEnabled) {
			return;
		}

		let isMounted = true;

		void canPersistOnboardingHoldoutState().then((canPersistSeenState) => {
			if (!isMounted) {
				return;
			}

			setPassesOnboardingHoldoutEligibility(canPersistSeenState && passesOnboardingHoldout());
		});

		return () => {
			isMounted = false;
		};
	}, [isCookiePolicyGateEnabled]);

	const isFeatureGateEnabled = useMemo(() => {
		if (product === 'jira') {
			return fg('rovo_onboarding_modal_jira_chat_fg');
		} else if (product === 'confluence') {
			return fg('rovo_onboarding_modal_confluence_chat_fg');
		}

		return false; // default case, shouldn't ever be reached
	}, [product]);

	const isEligiblePage = ELIGIBLE_PAGES.includes(pageContextType);
	const isEligibleProduct = ELIGIBLE_PRODUCTS.includes(product);

	// Track if chat has been opened
	useEffect(() => {
		if (isChatOpen && !hasOpenedChatRef.current) {
			hasOpenedChatRef.current = true;
		}
	}, [isChatOpen]);

	// Dwell timer effect - only runs when basic eligibility is met
	useEffect(() => {
		const shouldSkipTimer =
			hasOpenedChatRef.current ||
			!isEligiblePage ||
			!isEligibleProduct ||
			!passesOnboardingHoldoutEligibility ||
			!isFeatureGateEnabled ||
			!isRovoEnabled;

		if (shouldSkipTimer) {
			return;
		}

		// Reset dwell status when route changes
		setHasDwelled(false);

		// Clear any existing timer
		if (routeDwellTimerRef.current) {
			clearTimeout(routeDwellTimerRef.current);
			routeDwellTimerRef.current = null;
		}

		// Start new timer for current route
		routeDwellTimerRef.current = setTimeout(() => {
			setHasDwelled(true);
			routeDwellTimerRef.current = null;
		}, DWELL_TIME_MS);

		// Cleanup on unmount or route change
		return () => {
			if (routeDwellTimerRef.current) {
				clearTimeout(routeDwellTimerRef.current);
				routeDwellTimerRef.current = null;
			}
		};
	}, [
		pageContextType,
		isRovoEnabled,
		isFeatureGateEnabled,
		isEligiblePage,
		isEligibleProduct,
		passesOnboardingHoldoutEligibility,
	]);

	const isEligible = useMemo(
		() =>
			isRovoEnabled &&
			isEligiblePage &&
			isEligibleProduct &&
			isFeatureGateEnabled &&
			hasDwelled &&
			!isChatOpen &&
			passesOnboardingHoldoutEligibility,
		[
			isRovoEnabled,
			isFeatureGateEnabled,
			isEligiblePage,
			isEligibleProduct,
			hasDwelled,
			isChatOpen,
			passesOnboardingHoldoutEligibility,
		],
	);

	useEffect(() => {
		if (isEligible && !hasOpenedChatRef.current) {
			const cohort = FeatureGates.getExperimentValue<string>(
				'rovo_onboarding_modal_jira_chat',
				'cohort',
				'',
			);

			if (
				cohort === 'variation' ||
				FeatureGates.getExperimentValue<boolean>(
					'rovo_onboarding_modal_confluence_chat',
					'isEnabled',
					false,
				) === true
			) {
				hasOpenedChatRef.current = true;

				publish({
					type: 'chat-draft',
					product,
					source: 'ai-mate-onboarding',
					openChat: true,
					openChatMode: 'mini-modal',
				});

				recordOnboardingExposureForHoldout();

				setIsChatOnboardingMiniModalOpen(true);
			}
		}
	}, [isEligible, product, setIsChatOnboardingMiniModalOpen, publish]);
}
